import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useTranslation } from '../hooks/useTranslation';

const Register = () => {
    const navigate = useNavigate();
    const { t, locale, setLocale } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors },
    } = useForm();

    const password = watch('password');

    const getRoleBasedRedirect = (role) => {
        const roleRedirects = {
            employee: '/employee/home',
            company: '/company/vacancies',
            recruitment_company: '/company/vacancies',
            admin: '/dashboard',
        };
        return roleRedirects[role] || '/employee/home';
    };

    const onSubmit = async (data) => {
        try {
            setLoading(true);
            // PhoneInput returns the full phone number with country code (e.g., +201007988964)
            const fullPhone = data.phone || '';
            const registrationData = {
                ...data,
                phone: fullPhone,
            };
            const response = await authService.register(registrationData);
            if (response.success) {
                if (response.token) {
                    toast.success(t('register.registerSuccess'));
                    const redirectPath = getRoleBasedRedirect(response.user?.role);
                    navigate(redirectPath);
                } else {
                    toast.success(t('register.registerSuccessVerify'));
                    // Navigate to verify email page with email
                    navigate('/verify-email', { state: { email: data.email } });
                }
            } else {
                toast.error(t('register.registerError'));
            }
        } catch (error) {
            console.error('Registration error:', error);
            const message = error.response?.data?.message || error.message || t('register.registerError');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const toggleLanguage = () => {
        const newLocale = locale === 'ar' ? 'en' : 'ar';
        setLocale(newLocale);
    };

    return (
        <div className="login-page">
            {/* Language Switcher */}
            <div style={{
                position: 'absolute',
                top: '20px',
                right: locale === 'ar' ? '20px' : 'auto',
                left: locale === 'en' ? '20px' : 'auto',
                zIndex: 1000
            }}>
                <button
                    onClick={toggleLanguage}
                    style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#333',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                >
                    <span>🌐</span>
                    <span>{locale === 'ar' ? 'EN' : 'ع'}</span>
                </button>
            </div>

            {/* Header with gradient */}
            <div className="login-header">
                <div className="login-header-content text-center">
                <img src="/images/logo.png" alt="logo" className="login-header-logo" width={135} height={135} />
                <h1 className="app-title">TAWZEEF</h1>
                </div>
            </div>

            {/* Main register card */}
            <div className="login-card">
                <h2 className="welcome-text">{t('register.title')}</h2>

                <form onSubmit={handleSubmit(onSubmit)} className="login-form" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                    {/* Name Field */}
                    <div className="form-group-rtl">
                        <label htmlFor="name" className="form-label-rtl">{t('register.name')}</label>
                        <input
                            type="text"
                            id="name"
                            className="password-input form-control p-3"
                            placeholder={t('register.namePlaceholder')}
                            {...register('name', { required: t('register.nameRequired') })}
                        />
                        {errors.name && <span className="error-message-rtl">{errors.name.message}</span>}
                    </div>

                    {/* Email Field */}
                    <div className="form-group-rtl">
                        <label htmlFor="email" className="form-label-rtl">{t('register.email')}</label>
                        <input
                            type="email"
                            id="email"
                            className="password-input form-control p-3"
                            placeholder={t('register.emailPlaceholder')}
                            {...register('email', {
                                required: t('register.emailRequired'),
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: t('register.emailInvalid'),
                                },
                            })}
                        />
                        {errors.email && <span className="error-message-rtl">{errors.email.message}</span>}
                    </div>

                    {/* Phone Number Field */}
                    <div className="form-group-rtl">
                        <label htmlFor="phone" className="form-label-rtl">{t('register.phone')}</label>
                        <div className="phone-input-wrapper">
                            <Controller
                                name="phone"
                                control={control}
                                rules={{ required: t('register.phoneRequired') }}
                                render={({ field: { onChange, value } }) => (
                                    <PhoneInput
                                        international
                                        defaultCountry="EG"
                                        value={value}
                                        onChange={onChange}
                                        className="phone-input-custom"
                                    />
                                )}
                            />
                        </div>
                        {errors.phone && <span className="error-message-rtl">{errors.phone.message}</span>}
                    </div>

                    {/* Password Field */}
                    <div className="form-group-rtl">
                        <label htmlFor="password" className="form-label-rtl">{t('register.password')}</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                className="password-input"
                                placeholder={t('register.passwordPlaceholder')}
                                {...register('password', {
                                    required: t('register.passwordRequired'),
                                    minLength: {
                                        value: 8,
                                        message: t('register.passwordMinLength'),
                                    },
                                })}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {errors.password && <span className="error-message-rtl">{errors.password.message}</span>}
                    </div>

                    {/* Confirm Password Field */}
                    <div className="form-group-rtl">
                        <label htmlFor="password_confirmation" className="form-label-rtl">{t('register.confirmPassword')}</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="password_confirmation"
                                className="password-input"
                                placeholder={t('register.confirmPasswordPlaceholder')}
                                {...register('password_confirmation', {
                                    required: t('register.confirmPasswordRequired'),
                                    validate: (value) =>
                                        value === password || t('register.confirmPasswordMismatch'),
                                })}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {errors.password_confirmation && (
                            <span className="error-message-rtl">{errors.password_confirmation.message}</span>
                        )}
                    </div>

                    {/* User Role Field */}
                    <div className="form-group-rtl">
                        <label htmlFor="role" className="form-label-rtl">{t('register.role')}</label>
                        <select id="role" className="password-input form-select p-2 px-5" {...register('role', { required: t('register.roleRequired') })}>
                            <option value="employee">{t('register.roleEmployee')}</option>
                            <option value="company">{t('register.roleCompany')}</option>
                            <option value="recruitment_company">{t('register.roleRecruitmentCompany')}</option>
                        </select>
                    </div>

                    {/* Register Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="login-button"
                    >
                        {loading ? t('register.registerButtonLoading') : t('register.registerButton')}
                    </button>

                    {/* Login Link */}
                    <p className="create-account-link">
                        {t('register.hasAccount')}{' '}
                        <Link to="/login">{t('register.loginLink')}</Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default Register;

